/**
 * MandatCommissaireComptesService — mandats CAC (`/api/v1/mandats-commissaires-comptes/`).
 *
 * Un mandat porte la **paire** depuis la refonte d'octobre 2026 : titulaire et suppléant, avec
 * l'acte qui les désigne. PATCH = remplacement d'un porteur (par rôle) ou modification du mandat
 * (acte, mandataire, suppléant). Les durées (`date_fin`, `rang`, exercices restants) sont
 * calculées par le serveur. Les filtres `societe` et `date_fin__gte` sont servis par l'API
 * (BE-082 résolue) : plus aucun filtrage client.
 */

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { DrfPaginatedResponse } from '../../../../core/models/api.model';
import { buildHttpParams, QueryFilters } from '../../../../shared/core/services/pagination.util';
import { isolate } from '../../../../shared/rx';
import {
    MandatCommissaireAuxComptes,
    MandatCommissaireAuxComptesCreateRequest,
    PatchedMandatCommissaireAuxComptesPartialUpdateRequest,
} from '../models/cac-api.model';

/** Filtres serveur exposés par `GET /mandats-commissaires-comptes/`. */
export type MandatCacListFilters = QueryFilters & {
    page?: number;
    per_page?: number;
    /** Mandats d'une société — filtre serveur depuis la refonte d'octobre 2026 (BE-082 résolue). */
    societe?: string;
    /** Les porteurs se filtrent par rôle : un mandat porte le titulaire et le suppléant. */
    personne_physique_titulaire?: string;
    personne_physique_suppleant?: string;
    /** Une personne, quel que soit son rôle dans le mandat — évite une lecture par rôle. */
    is_titulaire_or_suppleant?: string;
    cabinet_titulaire?: string;
    cabinet_suppleant?: string;
    periode_exercice?: string;
    periode_exercice__icontains?: string;
    date_designation__gte?: string;
    date_designation__lte?: string;
    /** Échéance au plus tôt : alertes 30/60/90 j, et mandats en cours quand la borne est le jour même. */
    date_fin__gte?: string;
    date_fin__lte?: string;
};

@Injectable({ providedIn: 'root' })
export class MandatCommissaireComptesService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = `${environment.apiUrl}/mandats-commissaires-comptes/`;

    list(filters: MandatCacListFilters = {}): Observable<DrfPaginatedResponse<MandatCommissaireAuxComptes>> {
        return this.http.get<DrfPaginatedResponse<MandatCommissaireAuxComptes>>(this.endpoint, {
            params: buildHttpParams(filters),
        });
    }

    retrieve(id: string): Observable<MandatCommissaireAuxComptes> {
        return this.http.get<MandatCommissaireAuxComptes>(`${this.endpoint}${id}/`);
    }

    /** Plusieurs mandats par identifiant ; un mandat inaccessible est omis plutôt que de faire échouer l'ensemble. */
    retrieveMany(ids: string[]): Observable<MandatCommissaireAuxComptes[]> {
        if (!ids.length) {
            return of([]);
        }
        return forkJoin(ids.map((id) => this.retrieve(id).pipe(isolate<MandatCommissaireAuxComptes | null>(null)))).pipe(
            map((mandats) => mandats.filter((m): m is MandatCommissaireAuxComptes => m !== null)),
        );
    }

    /**
     * Désignation : un acte, un titulaire, son suppléant éventuel. Le mandat créé porte la paire
     * entière — il n'existe plus de création rôle par rôle.
     */
    create(payload: MandatCommissaireAuxComptesCreateRequest): Observable<MandatCommissaireAuxComptes> {
        return this.http.post<MandatCommissaireAuxComptes>(this.endpoint, payload);
    }

    /** PATCH — remplacement (remplaçant, prise d'effet, motif) ou modification de l'acte / du suppléant. */
    partialUpdate(id: string, payload: PatchedMandatCommissaireAuxComptesPartialUpdateRequest): Observable<MandatCommissaireAuxComptes> {
        return this.http.patch<MandatCommissaireAuxComptes>(`${this.endpoint}${id}/`, payload);
    }

    destroy(id: string): Observable<void> {
        return this.http.delete<void>(`${this.endpoint}${id}/`);
    }
}
